/**
 * Date-range maths for the analytics page.
 *
 * The old dashboard gave every chart its own "Time Period" dropdown, so the
 * same page could show stamps for this month next to rewards for this year.
 * Here the range is chosen once at the top of the page and passed down, which
 * is why all of this lives in one place instead of inside each chart.
 *
 * Everything is computed in the browser's local timezone: a merchant reads
 * "today" as their trading day, not as UTC.
 */

export type PeriodKey = '7d' | '30d' | '90d' | '12m' | 'all' | 'custom';

export interface DateRange {
  start: Date;
  end: Date;
}

/** Granularity the time-series charts bucket into. */
export type Bucket = 'day' | 'week' | 'month';

export const PERIOD_OPTIONS: { key: PeriodKey; label: string }[] = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
  { key: '12m', label: '12 months' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom' }
];

export const startOfDay = (date: Date) => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

export const endOfDay = (date: Date) => {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
};

export const addDays = (date: Date, days: number) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/** Monday-based, to match how a trading week is usually read. */
export const startOfWeek = (date: Date) => {
  const result = startOfDay(date);
  const weekday = (result.getDay() + 6) % 7;
  result.setDate(result.getDate() - weekday);
  return result;
};

export const startOfMonth = (date: Date) => {
  const result = startOfDay(date);
  result.setDate(1);
  return result;
};

/** `YYYY-MM-DD` in local time — `toISOString` would shift the day near midnight. */
export const toDateInput = (date: Date) => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/** Parses a `<input type="date">` value as a local date, not as UTC midnight. */
export const fromDateInput = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const dayCount = (range: DateRange) =>
  Math.max(
    1,
    Math.round((startOfDay(range.end).getTime() - startOfDay(range.start).getTime()) / 86400000) + 1
  );

export interface ResolveOptions {
  /** Earliest event on record, used as the start of the all-time range. */
  earliest?: Date;
  customStart?: string;
  customEnd?: string;
}

/**
 * Turns the selected period into a concrete range. All-time with no data yet
 * falls back to the last 30 days so the axes still have something to draw.
 */
export const resolveRange = (period: PeriodKey, options: ResolveOptions = {}): DateRange => {
  const today = endOfDay(new Date());

  switch (period) {
    case '7d':
      return { start: startOfDay(addDays(today, -6)), end: today };
    case '30d':
      return { start: startOfDay(addDays(today, -29)), end: today };
    case '90d':
      return { start: startOfDay(addDays(today, -89)), end: today };
    case '12m': {
      const start = startOfDay(today);
      start.setFullYear(start.getFullYear() - 1);
      start.setDate(start.getDate() + 1);
      return { start, end: today };
    }
    case 'all':
      return {
        start: options.earliest ? startOfDay(options.earliest) : startOfDay(addDays(today, -29)),
        end: today
      };
    case 'custom': {
      const start = options.customStart ? fromDateInput(options.customStart) : null;
      const end = options.customEnd ? fromDateInput(options.customEnd) : null;
      return {
        start: startOfDay(start ?? addDays(today, -29)),
        end: endOfDay(end ?? today)
      };
    }
  }
};

/**
 * The equally long window immediately before `range`, which is what the
 * headline figures compare against. A 30-day period is compared with the 30
 * days before it, never with "last calendar month".
 */
export const previousRange = (range: DateRange): DateRange => {
  const days = dayCount(range);
  const end = endOfDay(addDays(startOfDay(range.start), -1));
  return { start: startOfDay(addDays(end, -(days - 1))), end };
};

export const isWithin = (value: string | Date | null | undefined, range: DateRange) => {
  if (!value) return false;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return false;
  return time >= range.start.getTime() && time <= range.end.getTime();
};

/** Keeps the bar count readable: roughly 12–90 buckets across any range. */
export const bucketFor = (range: DateRange): Bucket => {
  const days = dayCount(range);
  if (days <= 62) return 'day';
  if (days <= 365) return 'week';
  return 'month';
};

export const bucketStart = (date: Date, bucket: Bucket) => {
  if (bucket === 'month') return startOfMonth(date);
  if (bucket === 'week') return startOfWeek(date);
  return startOfDay(date);
};

/** Stable key for grouping; also sorts correctly as a string. */
export const bucketKey = (date: Date, bucket: Bucket) => toDateInput(bucketStart(date, bucket));

/** Every bucket in the range, including the empty ones, oldest first. */
export const enumerateBuckets = (range: DateRange, bucket: Bucket): string[] => {
  const keys: string[] = [];
  const cursor = bucketStart(range.start, bucket);
  const last = bucketStart(range.end, bucket);

  // Guard the loop on a bucket count rather than on the cursor alone, so a
  // pathological range can never spin forever.
  const limit = 1000;
  while (cursor <= last && keys.length < limit) {
    keys.push(toDateInput(cursor));
    if (bucket === 'month') cursor.setMonth(cursor.getMonth() + 1);
    else cursor.setDate(cursor.getDate() + (bucket === 'week' ? 7 : 1));
  }

  return keys;
};

export const formatBucketTick = (key: string, bucket: Bucket) => {
  const date = fromDateInput(key);
  if (!date) return key;
  if (bucket === 'month') {
    return date.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
  }
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

export const formatBucketLabel = (key: string, bucket: Bucket) => {
  const date = fromDateInput(key);
  if (!date) return key;
  if (bucket === 'month') {
    return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }
  const formatted = date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
  if (bucket === 'week') return `Week of ${formatted}`;
  return formatted;
};

export const formatRange = (range: DateRange) => {
  const sameYear = range.start.getFullYear() === range.end.getFullYear();
  const start = range.start.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' })
  });
  const end = range.end.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
  return `${start} – ${end}`;
};

/**
 * Percentage change against the comparison window. Returns null when there is
 * nothing to compare against — showing "+100%" because the previous period was
 * empty says less than showing nothing.
 */
export const percentChange = (current: number, previous: number): number | null => {
  if (previous === 0) return null;
  return ((current - previous) / previous) * 100;
};
